import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, computed, DestroyRef, effect, inject, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { faDesktop, faMobile, faTablet } from '@fortawesome/pro-regular-svg-icons';

import { PreviewDevice, SourceVariables } from '@empathy/survey/base';

import { PreviewViewerHostConfig } from '../../models/preview-viewer-host-config.interface';
import { SurveyEditorService } from '../../services/survey-editor.service';
import { SurveyPreviewGenerationCoordinator } from '../../services/survey-preview-generation-coordinator.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

const PREVIEW_SESSION_CLEANUP_STORAGE_KEY = 'empathy.survey-preview.cleanup-session-id';

@Component({
  selector: 'empathy-survey-full-preview',
  templateUrl: 'survey-full-preview.component.html',
  styleUrls: ['./survey-full-preview.component.scss', './_survey-full-preview-theme.component.scss'],
  providers: [SurveyPreviewGenerationCoordinator]
})
export class SurveyFullPreviewComponent implements OnInit, OnDestroy {
  selectedActiveDevice$ = input<PreviewDevice>(undefined, { alias: 'selectedActiveDevice' });
  variables$ = input<SourceVariables | null, SourceVariables | null>(null, {
    alias: 'variables',
    transform: (value) => (!value || Object.keys(value).length === 0 ? null : value)
  });

  busyChange = output<boolean>();

  readonly activeDevice$ = signal<PreviewDevice>(PreviewDevice.Desktop);
  readonly displayedDevice$ = computed(() => this.selectedActiveDevice$() ?? this.activeDevice$());
  readonly hostConfig$ = signal<PreviewViewerHostConfig | null>(null);
  readonly isBusy$ = signal(false);
  readonly showDeviceToolbar$ = computed(() => !this.selectedActiveDevice$());

  readonly faDesktop = faDesktop;
  readonly faMobile = faMobile;
  readonly faTablet = faTablet;
  readonly previewDevice = PreviewDevice;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly generationCoordinator = inject(SurveyPreviewGenerationCoordinator);
  private readonly surveyEditorService = inject(SurveyEditorService);

  private initialized = false;
  private observedVariables: SourceVariables | null = null;
  private previewSessionId: string | null = this.readPreviewSessionCleanupId();

  constructor() {
    effect(() => this.handleSourceVariableChange(this.variables$()));
  }

  ngOnInit(): void {
    this.observedVariables = this.variables$();
    this.recreateHost(this.observedVariables);
    this.initialized = true;
  }

  ngOnDestroy(): void {
    this.busyChange.emit(false);
  }

  onHostBusyChange(isBusy: boolean): void {
    this.isBusy$.set(isBusy);
    this.busyChange.emit(isBusy);
  }

  onRestartRequested(): void {
    if (!this.isBusy$()) {
      this.recreateHost(this.hostConfig$()?.variables ?? this.variables$());
    }
  }

  onSessionCreated(previewSessionId: string): void {
    this.previewSessionId = previewSessionId;
    this.writePreviewSessionCleanupId(previewSessionId);
  }

  private handleSourceVariableChange(variables: SourceVariables | null): void {
    if (!this.initialized || variables === this.observedVariables) {
      return;
    }

    this.observedVariables = variables;
    if (this.isBusy$()) {
      return;
    }

    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Apply Variables',
          confirmText: 'Reload survey applying the new source variables?',
          confirmBtn: 'Apply',
          color: 'primary'
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.recreateHost(variables);
        }
      });
  }

  private recreateHost(variables: SourceVariables | null): void {
    const generation = this.generationCoordinator.activateNextGeneration();
    const replacesPreviewSessionId = this.previewSessionId ?? undefined;

    if (this.hostConfig$()) {
      this.hostConfig$.set(null);
      this.cdr.detectChanges();
    }

    this.onHostBusyChange(true);
    this.hostConfig$.set({
      generation,
      authoredSurveyJson: this.surveyEditorService.surveyEditor$().JSON,
      variables,
      languageCode: 'en-GB',
      replacesPreviewSessionId
    });
  }

  private readPreviewSessionCleanupId(): string | null {
    try {
      return this.document.defaultView?.sessionStorage.getItem(PREVIEW_SESSION_CLEANUP_STORAGE_KEY) ?? null;
    } catch {
      return null;
    }
  }

  private writePreviewSessionCleanupId(previewSessionId: string): void {
    try {
      this.document.defaultView?.sessionStorage.setItem(PREVIEW_SESSION_CLEANUP_STORAGE_KEY, previewSessionId);
    } catch {
      // Preview still works without reload cleanup when browser storage is unavailable.
    }
  }
}
